import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getDomain } from "../../redux/reducers/domainReducer";
import {
  getCoursesBySkillPath,
  selectSkillPathCourses,
} from "../../redux/reducers/courseReducer";
import {
  getDomainSkillPaths,
  selectDomainSkillPathsBySkillPathLevel,
} from "../../redux/reducers/skillPathReducer";
import {
  getSkillPathLevels,
  selectDomainSkillPathLevels,
} from "../../redux/reducers/skillPathLevelsReducer";
import {
  SFGridContainer,
  SFGridItem,
  SFContentContainer,
  SFStack,
  SFLoadingPage,
  SFTabs,
  SFUseStyled,
} from "@sf-core";
import CourseList from "../../components/list";
import { SkillPathVerticalTabs } from "../../components/tabs";
import BasicDetails from "../../components/basicDetails";
import { userConfigurationActions } from "../../redux/reducers/userConfigurationReducer";

const { styled } = SFUseStyled();

const StyledSFStack = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "scrollable",
})(({ theme, height, flex, maxWidth, scrollable, paddingRight }) => ({
  ...(paddingRight && { paddingRight: theme.spacing(paddingRight) }),
  ...(height && height),
  ...(maxWidth && maxWidth),
  ...(flex && { flex: "1 0 auto" }),
  //adds page scrollbar in 1229x680
  minHeight: "250px",
  ...(scrollable && {
    height: 0,
    ...theme.scrollable,
  }),
}));

const StyledGridItem = styled(SFGridItem, {
  shouldForwardProp: (prop) => prop !== "scrollable",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("lg")]: {
    minHeight: "450px",
  },
}));

const AcademyScreen = () => {
  const dispatch = useDispatch();
  let { id } = useParams();
  id = id.toLowerCase();
  
  const [currentState, setCurrentState] = useState({
    currentSkillPathLevelId: null,
    currentSkillPathId: null,
  });

  const academy = useSelector((state) =>
    state.domains.find((x) => x.id === id)
  );

  const skillPathLevels = useSelector((state) =>
    selectDomainSkillPathLevels(state, id)
  );

  const skillPaths = useSelector((state) =>
    selectDomainSkillPathsBySkillPathLevel(
      state,
      id,
      currentState.currentSkillPathLevelId
    )
  );

  const courses = useSelector((state) =>
    selectSkillPathCourses(state, currentState.currentSkillPathId)
  );

  const lastAccessed = useSelector((state) =>
    state.userConfiguration.lastAccessed.find((x) => x.domainId === id)
  );

  useEffect(() => {
    dispatch(getDomain(id));
    dispatch(getSkillPathLevels());
    dispatch(getDomainSkillPaths(id));
  }, []);

  useEffect(() => {
    if (skillPathLevels.length > 0) {
      setCurrentState((prv) => ({
        currentSkillPathLevelId:
          lastAccessed?.skillPathLevelId ?? skillPathLevels[0].id,
        currentSkillPathId: null,
      }));
    }
  }, [skillPathLevels.length]);

  useEffect(() => {
    if (skillPaths.length !== 0) {
      setCurrentState((prv) => ({
        ...prv,
        currentSkillPathId: lastAccessed?.skillPathId ?? skillPaths[0].id,
      }));

      if (lastAccessed?.skillPathId) {
        dispatch(userConfigurationActions.removeLastAccessed({ domainId: id }));
      }
    }
  }, [skillPaths.length, currentState.currentSkillPathLevelId]);

  useEffect(() => {
    if (currentState.currentSkillPathId) {
      dispatch(getCoursesBySkillPath(currentState.currentSkillPathId));
    }
  }, [currentState.currentSkillPathId]);

  if (!academy) {
    return (
      <SFContentContainer>
        <SFLoadingPage />
      </SFContentContainer>
    );
  }

  return (
    <SFContentContainer
      backgroundImage={academy.backgroundImage}
      showTopMargin
      showSideMargins
    >
      <SFGridContainer sx={{ flex: 1 }} alignItems="stretch" spacing={10}>
        <SFGridItem xs={12} md={6}>
          <SFStack spacing={6} height="100%">
            <BasicDetails item={academy} />
            <StyledSFStack spacing={4} flex={true}>
              {(!currentState.currentSkillPathLevelId ||
                skillPathLevels.length === 0) && <SFLoadingPage />}
              {currentState.currentSkillPathLevelId &&
                skillPathLevels.length !== 0 && (
                <React.Fragment>
                  <SFTabs
                    key="primary"
                    items={skillPathLevels}
                    value={currentState.currentSkillPathLevelId}
                    handleChange={(currentSkillPathLevelId) => {
                      setCurrentState((cur) => ({
                        currentSkillPathId: null,
                        currentSkillPathLevelId,
                      }));
                    }}
                  />
                  {skillPaths && skillPaths.length !== 0 && (
                    <SkillPathVerticalTabs
                      items={skillPaths}
                      value={
                        currentState.currentSkillPathId ?? skillPaths[0].id
                      }
                      handleChange={(currentSkillPathId) => {
                        setCurrentState((cur) => ({
                          ...cur,
                          currentSkillPathId,
                        }));
                      }}
                      loading={courses.length === 0}
                      scrollable
                    />
                  )}
                </React.Fragment>
              )}
            </StyledSFStack>
          </SFStack>
        </SFGridItem>
        <StyledGridItem xs={12} md={6}>
          {courses.length === 0 ? (
            <SFLoadingPage />
          ) : (
            <CourseList
              domain={academy}
              items={courses}
              skillPathLevelId={currentState.currentSkillPathLevelId}
              courseDescriptionLines={6}
            />
          )}
        </StyledGridItem>
      </SFGridContainer>
    </SFContentContainer>
  );
};

export default AcademyScreen;
